import type React from "react";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import { createStyles, makeStyles } from "@mui/styles";
import { type Theme } from "@mui/material/styles";
import { CreateScheduleWizard } from "../custom-schedule/CreateScheduleWizard";

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		root: {
			"& .MuiSelect-root": {
				backgroundColor: "#FFF",
			},
		},
		formControl: {
			width: "99%",
			marginTop: "8px",
			marginBottom: "4px",
		},
		inputEditable: {
			"& .MuiOutlinedInput-root": {
				boxSizing: "border-box",
			},
			"& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
				border: `2px solid ${theme.palette.primary.main}`,
			},
			"& .MuiOutlinedInput-input": {
				padding: "8.5px 14px",
			},
		},
		cronText: {
			fontSize: "0.75rem",
			color: "rgba(0, 0, 0, 0.54)",
			marginTop: "4px",
			marginLeft: "8px",
		},
	}),
);

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
	PaperProps: {
		style: {
			maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
			width: 250,
		},
	},
};

const SchedulesCodes = [
	{
		SchedulingId: 1,
		SchedulingNameCode: "Immediate",
		SchedulingPolicyCode: "I",
	},
	{
		SchedulingId: 2,
		SchedulingNameCode: "Period after last close",
		SchedulingPolicyCode: "C",
	},
	{
		SchedulingId: 3,
		SchedulingNameCode: "Period after last open",
		SchedulingPolicyCode: "O",
	},
	{
		SchedulingId: 5,
		SchedulingNameCode: "Down days",
		SchedulingPolicyCode: "D",
	},
	// TODO: Enable when templates-worker service is deployed
	// Custom (CRON) scheduling requires the templates-worker .NET service
	// which is not currently running in swv-cmms. The wizard code is kept
	// intact for future use. See TEMPLATES-SYSTEM.md for details.
	// {
	// 	SchedulingId: 4,
	// 	SchedulingNameCode: "Custom",
	// 	SchedulingPolicyCode: "P",
	// },
];

type Props = {
	value: string;
	onChange: (value: string, cronExpression?: string) => void;
	cronExpression: string | null;
	onCronExpressionChange: (expression: string) => void;
	isModalOpen: boolean;
	setIsModalOpen: (isOpen: boolean) => void;
	hideScheduleStart?: (hide: boolean) => void;
	onScheduleStartChange?: (date: Date) => void;
	scheduleStart?: Date | null;
};

export const ScheduleCodePicker = ({
	value,
	onChange,
	cronExpression,
	isModalOpen,
	onCronExpressionChange,
	setIsModalOpen,
	hideScheduleStart,
	onScheduleStartChange,
	scheduleStart,
}: Props) => {
	const classes = useStyles();

	const handleChange = (event: SelectChangeEvent<string>) => {
		const newValue = event.target.value;
		onChange(newValue);

		if (newValue === "P") {
			setIsModalOpen(true);
			if (hideScheduleStart) {
				hideScheduleStart(true);
			}
		} else {
			if (hideScheduleStart) {
				hideScheduleStart(false);
			}
			onCronExpressionChange("");
		}
	};

	const handleWizardSave = (cronExp: string, scheduleStart: Date) => {
		onCronExpressionChange(cronExp);

		if (onScheduleStartChange) {
			onScheduleStartChange(scheduleStart);
		}

		setIsModalOpen(false);
	};

	const handleWizardClose = (shouldUpdate: boolean) => {
		if (!shouldUpdate && !cronExpression && value === "P") {
			onChange("C");
		}

		setIsModalOpen(false);
	};

	return (
		<div className={classes.root} style={{ position: "relative" }}>
			{SchedulesCodes.length > 0 && (
				<FormControl
					variant="outlined"
					size="small"
					className={`${classes.formControl} ${classes.inputEditable}`}
				>
					<InputLabel id="recurrence-select-label">Recurrence Type</InputLabel>
					<Select
						labelId="recurrence-select-label"
						id="recurrence-select"
						autoWidth={true}
						value={value}
						onChange={handleChange}
						label="Recurrence Type"
						MenuProps={MenuProps}
					>
						{SchedulesCodes.map((sc) => (
							<MenuItem
								key={`ScheduleId-${sc.SchedulingId}`}
								value={sc.SchedulingPolicyCode}
								onClick={() => {
									if (
										sc.SchedulingPolicyCode === value &&
										sc.SchedulingPolicyCode === "P"
									) {
										setIsModalOpen(true);
									}
								}}
							>
								<div style={{ display: "flex", alignItems: "center" }}>
									<div>{sc.SchedulingNameCode}</div>
								</div>
							</MenuItem>
						))}
					</Select>
				</FormControl>
			)}

			{value === "P" && (
				<CreateScheduleWizard
					show={isModalOpen}
					onHide={handleWizardClose}
					onSave={handleWizardSave}
					initialCronExpression={cronExpression || ""}
					initialScheduleStart={scheduleStart}
				/>
			)}
		</div>
	);
};
